import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DuplicateConflictError, type DuplicateCandidate } from '../api/client'
import { companiesApi, type CompanyRequest } from '../api/companies'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import {
  FormGrid,
  Button,
  ButtonLink,
  Callout,
  Card,
  CardContent,
  CardHeader,
  LoadingState,
  Page as PageShell,
  PageHeader,
  Select,
  StatusPill,
  TextField,
} from '../components/ui'

/** API route segment for each `DuplicateCandidate.entityType` value the backend's
 * duplicate-detection service can return (see `DuplicateCheckModels.cs` /
 * `CustomerDuplicateDetectionService.cs`) — irregular plurals (Person → people,
 * Company → companies) so this can't be derived by lowercasing + 's'. Same mapping
 * `CustomerFormPage.tsx` uses (there is no shared module for it in this repo). */
const ENTITY_TYPE_API_SEGMENT: Record<string, string> = {
  Person: 'people',
  Company: 'companies',
  Contact: 'contacts',
  Customer: 'customers',
}

function candidateApiUrl(c: DuplicateCandidate): string {
  const segment = ENTITY_TYPE_API_SEGMENT[c.entityType] ?? c.entityType.toLowerCase()
  return `/api/${segment}/${c.id}`
}

interface Props {
  mode: 'create' | 'edit'
}

interface FormState {
  name: string
  vatNumber: string
  fiscalCode: string
  email: string
  phone: string
  address: string
  city: string
  province: string
  notes: string
  assignedAgentId: string
  // TASK-0078: real DB columns (Company.SdiCode/Pec/PostalCode/Country) that were readable via
  // CompanyDetail/CompanyDetailPage.tsx but had no form field to ever set them.
  sdiCode: string
  pec: string
  postalCode: string
  country: string
}

const EMPTY_FORM: FormState = {
  name: '',
  vatNumber: '',
  fiscalCode: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  province: '',
  notes: '',
  assignedAgentId: '',
  sdiCode: '',
  pec: '',
  postalCode: '',
  // Company.Country defaults to "Italia" on the entity; prefilling the same value here means a
  // company created without touching this field ends up the same way it always has, rather than
  // silently switching to an empty string now that Update actually writes Country (see the
  // nullability note on Company.Update).
  country: 'Italia',
}

export function CompanyFormPage({ mode }: Props) {
  const { id: idParam } = useParams<{ id: string }>()
  const id = parseInt(idParam ?? '0', 10)
  const navigate = useNavigate()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [duplicateConflict, setDuplicateConflict] = useState<
    { message: string; candidates: DuplicateCandidate[] } | null
  >(null)
  const [loadingData, setLoadingData] = useState(mode === 'edit')
  const [agents, setAgents] = useState<DirectoryUser[]>([])

  // TASK-0059: agent options for the "Agente assegnato" selector below. Filtered server-side to
  // role=EqualAgente (unlike UsersPage's own manager selector, which deliberately shows every
  // role since a "responsabile" can be anyone) — AssignedAgentId here is specifically meant to
  // name the agent-role user handling this company, so an unfiltered list would let a company be
  // "assigned" to e.g. an EqualBackOffice user, which is not what the field means.
  useEffect(() => {
    userDirectoryApi.list({ roles: ['EqualAgente'], activeOnly: false, limit: 200 })
      .then(setAgents)
      .catch(() => setAgents([]))
  }, [])

  useEffect(() => {
    if (mode !== 'edit') return
    setLoadingData(true)
    companiesApi
      .get(id)
      .then(company => {
        setForm({
          name: company.name ?? '',
          vatNumber: company.vatNumber ?? '',
          fiscalCode: company.fiscalCode ?? '',
          email: company.email ?? '',
          phone: company.phone ?? '',
          address: company.address ?? '',
          city: company.city ?? '',
          province: company.province ?? '',
          notes: company.notes ?? '',
          assignedAgentId: company.assignedAgentId != null ? String(company.assignedAgentId) : '',
          sdiCode: company.sdiCode ?? '',
          pec: company.pec ?? '',
          postalCode: company.postalCode ?? '',
          country: company.country ?? '',
        })
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
      .finally(() => setLoadingData(false))
  }, [mode, id])

  function setField(
    key: keyof FormState,
  ): (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void {
    return e => setForm(f => ({ ...f, [key]: e.target.value }))
  }

  async function handleSave(forceCreate = false): Promise<void> {
    setSaving(true)
    setError(null)
    if (!forceCreate) setDuplicateConflict(null)
    try {
      const body: CompanyRequest = { name: form.name || 'Nuova Azienda' }
      if (form.vatNumber) body.vatNumber = form.vatNumber
      if (form.fiscalCode) body.fiscalCode = form.fiscalCode
      if (form.email) body.email = form.email
      if (form.phone) body.phone = form.phone
      if (form.address) body.address = form.address
      if (form.city) body.city = form.city
      if (form.province) body.province = form.province
      if (form.notes) body.notes = form.notes
      if (form.assignedAgentId) body.assignedAgentId = Number(form.assignedAgentId)
      // TASK-0078: always sent (create AND edit) whenever populated — omitting these on an edit
      // that only touches an unrelated field (e.g. phone) would silently wipe them, since
      // Company.Update assigns every one of its parameters unconditionally (no per-field guard).
      if (form.sdiCode) body.sdiCode = form.sdiCode
      if (form.pec) body.pec = form.pec
      if (form.postalCode) body.postalCode = form.postalCode
      if (form.country) body.country = form.country
      if (mode === 'create' && forceCreate) body.forceCreate = true

      let resultId: number
      if (mode === 'edit') {
        await companiesApi.update(id, body)
        resultId = id
      } else {
        const res = await companiesApi.create(body)
        resultId = res.id
      }
      navigate('/companies/' + resultId)
    } catch (err) {
      if (err instanceof DuplicateConflictError) {
        setDuplicateConflict({ message: err.message, candidates: err.candidates })
        setSaving(false)
        return
      }
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio')
      setSaving(false)
    }
  }

  if (loadingData) {
    return <LoadingState />
  }

  return (
    <PageShell>
      <PageHeader
        back={{ to: '/companies', label: 'Aziende' }}
        icon="business"
        title={mode === 'create' ? 'Nuova Azienda' : 'Modifica Azienda'}
      />

      {duplicateConflict ? (
        <Callout tone="warning" icon="report" title={duplicateConflict.message}>
          <ul className="list-unstyled mb-3 ps-4">
            {duplicateConflict.candidates.map((c, i) => (
              <li key={`${c.entityType}-${c.id}-${i}`} className="mb-1">
                <StatusPill tone="neutral" className="me-2">{c.entityType}</StatusPill>
                ID {c.id} — campo corrispondente: <strong>{c.matchedField}</strong>
                {' '}
                <a
                  href={candidateApiUrl(c)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-2"
                >
                  Vedi record esistente
                </a>
                {(c.entityType === 'Company' || c.entityType === 'Contact' || c.entityType === 'Person') && (
                  <Button
                    variant="outlined"
                    size="sm"
                    className="ms-2"
                    onClick={() =>
                      navigate(
                        c.entityType === 'Company'
                          ? `/companies/${c.id}/edit`
                          : c.entityType === 'Person'
                            ? `/people/${c.id}/edit`
                            : `/contacts/${c.id}/edit`,
                      )
                    }
                  >
                    Usa questo record esistente
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <div className="d-flex gap-2">
            <Button
              variant="tonal"
              size="sm"
              disabled={saving}
              onClick={() => void handleSave(true)}
            >
              {saving ? 'Creazione...' : 'Crea comunque'}
            </Button>
            <Button variant="text" size="sm" disabled={saving} onClick={() => setDuplicateConflict(null)}>
              Annulla
            </Button>
          </div>
        </Callout>
      ) : (
        error && <Callout tone="error">{error}</Callout>
      )}

      {/* Informazioni Anagrafiche */}
      <Card variant="outlined">
        <CardHeader title="Informazioni anagrafiche" />
        <CardContent>
          <FormGrid columns={1}>
            <div>
              <TextField
                label="Ragione Sociale *"
                id="cof-name"
                type="text"
                required
                value={form.name}
                onChange={setField('name')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={3} className="mt-3">
            <div>
              <TextField
                label="Email"
                id="cof-email"
                type="email"
                value={form.email}
                onChange={setField('email')}
              />
            </div>
            <div>
              <TextField
                label="Telefono"
                id="cof-phone"
                type="text"
                value={form.phone}
                onChange={setField('phone')}
              />
            </div>
            <div>
              <Select
                label="Agente assegnato"
                id="cof-assignedAgentId"
                value={form.assignedAgentId}
                onChange={e => setForm(f => ({ ...f, assignedAgentId: e.target.value }))}
              >
                <option value="">Nessun agente</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            </div>
          </FormGrid>

          <FormGrid columns={4} className="mt-3">
            <div className="md-span-2">
              <TextField
                label="Indirizzo"
                id="cof-address"
                type="text"
                value={form.address}
                onChange={setField('address')}
              />
            </div>
            <div>
              <TextField
                label="Città"
                id="cof-city"
                type="text"
                value={form.city}
                onChange={setField('city')}
              />
            </div>
            <div>
              <TextField
                label="Provincia"
                id="cof-province"
                type="text"
                maxLength={2}
                value={form.province}
                onChange={e => setForm(f => ({ ...f, province: e.target.value.toUpperCase() }))}
              />
            </div>
          </FormGrid>
        </CardContent>
      </Card>

      {/* Dati Fiscali */}
      <Card variant="outlined">
        <CardHeader title="Dati fiscali" />
        <CardContent>
          <FormGrid>
            <div>
              <TextField
                label="Codice Fiscale"
                id="cof-fiscalCode"
                type="text"
                value={form.fiscalCode}
                onChange={setField('fiscalCode')}
              />
            </div>
            <div>
              <TextField
                label="P.IVA"
                id="cof-vatNumber"
                type="text"
                value={form.vatNumber}
                onChange={setField('vatNumber')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={4} className="mt-3">
            <div>
              <TextField
                label="PEC"
                id="cof-pec"
                type="email"
                value={form.pec}
                onChange={setField('pec')}
              />
            </div>
            <div>
              <TextField
                label="Codice SDI"
                id="cof-sdiCode"
                type="text"
                value={form.sdiCode}
                onChange={setField('sdiCode')}
              />
            </div>
            <div>
              <TextField
                label="CAP"
                id="cof-postalCode"
                type="text"
                value={form.postalCode}
                onChange={setField('postalCode')}
              />
            </div>
            <div>
              <TextField
                label="Nazione"
                id="cof-country"
                type="text"
                value={form.country}
                onChange={setField('country')}
              />
            </div>
          </FormGrid>
        </CardContent>
      </Card>

      {/* Note */}
      <Card variant="outlined">
        <CardHeader title="Note" />
        <CardContent>
          <TextField
            label="Note"
            labelHidden
            multiline
            id="cof-notes"
            rows={3}
            value={form.notes}
            onChange={setField('notes')}
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="d-flex gap-2">
        <Button
          variant="filled"
          icon="save"
          loading={saving}
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
        <ButtonLink to="/companies" variant="text">
          Annulla
        </ButtonLink>
      </div>
    </PageShell>
  )
}
